/**
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 * https://www.w3.org/TR/WCAG21/#contrast-minimum
 * @param hexColor - A hex color string (e.g., "#aabbcc" or "#abc").
 * @returns - "black" or "white" depending on which color has better contrast with the given hex color.
 */
export const getContrastTextColor = (hexColor: string) => {
  const hex = hexColor.replace('#', '');

  // Support both 3-digit (#abc) and 6-digit (#aabbcc) hex
  const fullHex = hex.length === 3
    ? hex.split('').map((c) => c + c).join('')
    : hex;

  const r = parseInt(fullHex.substring(0, 2), 16) / 255;
  const g = parseInt(fullHex.substring(2, 4), 16) / 255;
  const b = parseInt(fullHex.substring(4, 6), 16) / 255;

  const rLin = linearize(r);
  const gLin = linearize(g);
  const bLin = linearize(b);

  // Relative luminance (WCAG formula)
  const luminance = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;

  // WCAG contrast ratio threshold for normal text is 4.5:1, which corresponds to a luminance of 0.179.
  // Formula to calculate contrast: (L1 + 0.05) / (L2 + 0.05)
  // To get the threshold, white = 1.0, black = 0.0.
  // (1.0 + 0.05) / (L + 0.05) = (L + 0.05) / (0.0 + 0.05)
  // Solving for L gives us the threshold of 0.179.
  return luminance > 0.6 ? "black" : "white";
}

// Convert sRGB to linear RGB (gamma correction).
const linearize = (channel: number) =>
  channel <= 0.03928
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4);